import { Alert } from 'react-native';
import Config from 'react-native-config';
import base64 from 'react-native-base64';
import { Dispatch } from 'redux';
import {
  ActionPayloadRequest,
  AdapterSource,
  DFDHeadersObject,
  DispatcherAuthPrefix,
  DispatcherCategoryService,
  DispatcherEndpoint,
  DispatcherResource
} from 'modules/types/common';
import { DFDHeaders } from 'serverTypes/authentication';
import FileServices from 'services/FileServices';
import { getDispatcherUrl } from 'modules/utils/UrlUtils';
import { getDownloadHeadersWithCurrentAccountConsumerId } from 'lib/downloads/utils';
import { RootState } from 'store/types';
import { getDocumentDecorator } from 'store/utils';
import { ShareLabResults, TestResult, TestResultFocused } from '../types';
import { labDetailsDownloadDataSelector } from './selectors';

export const GET_LAB_HISTORICAL_MILLENNIUM = 'TEST_RESULTS.GET_LAB_HISTORICAL_MILLENNIUM';
export const GET_LAB_HISTORICAL_MILLENNIUM_SUCCESS =
  'TEST_RESULTS.GET_LAB_HISTORICAL_MILLENNIUM_SUCCESS';
export const GET_LAB_HISTORICAL_MILLENNIUM_FAIL = 'TEST_RESULTS.GET_LAB_HISTORICAL_MILLENNIUM_FAIL';

interface GetLabHistoricalMillennium {
  labName: string;
  loincCodes?: string;
  unit?: string;
}

export const getLabHistoricalMillennium = (
  params: GetLabHistoricalMillennium,
  adapterSource = AdapterSource.UTAH
) => {
  let client = 'testResults';
  const request: ActionPayloadRequest = {
    method: 'get',
    url: '/lab/historical',
    params: {
      source: 'MILLENNIUM',
      ...params
    }
  };

  if (!!Config.TEST_RESULTS_EXPANSION_ENABLED) {
    client = 'dispatcher';
    request.url = getDispatcherUrl(
      DispatcherCategoryService.HEALTH_RECORDS,
      DispatcherAuthPrefix.AUTHENTICATED,
      DispatcherResource.LABS,
      DispatcherEndpoint.HISTORICAL
    );
    request.headers = {
      [DFDHeaders.filterSource]: 'MILLENNIUM',
      [DFDHeaders.loincCodes]: params.loincCodes || '',
      [DFDHeaders.mhpAdapter]: adapterSource,
      [DFDHeaders.testResultName]: params.labName
    };
    delete request.params;
  }

  return {
    type: GET_LAB_HISTORICAL_MILLENNIUM,
    payload: {
      client,
      request
    }
  };
};

interface GetLabHistoricalCdr {
  panelName: string;
  labName: string;
  loincCode?: number;
  unit?: string;
}

export const GET_LAB_HISTORICAL_CDR = 'TEST_RESULTS.GET_LAB_HISTORICAL_CDR';
export const GET_LAB_HISTORICAL_CDR_SUCCESS = 'TEST_RESULTS.GET_LAB_HISTORICAL_CDR_SUCCESS';
export const GET_LAB_HISTORICAL_CDR_FAIL = 'TEST_RESULTS.GET_LAB_HISTORICAL_CDR_FAIL';

export const getLabHistoricalCdr = (
  params: GetLabHistoricalCdr,
  adapterSource = AdapterSource.UTAH
) => {
  let client = 'testResults';
  const request: ActionPayloadRequest = {
    method: 'get',
    url: '/lab/historical',
    params: {
      source: 'CDR',
      ...params
    }
  };

  if (!!Config.TEST_RESULTS_EXPANSION_ENABLED) {
    client = 'dispatcher';
    request.url = getDispatcherUrl(
      DispatcherCategoryService.HEALTH_RECORDS,
      DispatcherAuthPrefix.AUTHENTICATED,
      DispatcherResource.LABS,
      DispatcherEndpoint.HISTORICAL
    );
    request.headers = {
      [DFDHeaders.filterSource]: 'CDR',
      [DFDHeaders.panelName]: params.panelName,
      [DFDHeaders.mhpAdapter]: adapterSource,
      [DFDHeaders.testResultName]: params.labName
    };
    delete request.params;
  }

  return {
    type: GET_LAB_HISTORICAL_CDR,
    payload: {
      client,
      request
    }
  };
};

export const RESET_LAB_HISTORICAL_CDR = 'TEST_RESULTS.RESET_LAB_HISTORICAL_CDR';

export const resetLabHistoricalCdr = () => ({ type: RESET_LAB_HISTORICAL_CDR });

/**
 * Download Test Results
 */
export const DOWNLOAD_LAB_DETAILS = 'TEST_RESULTS.DOWNLOAD_LAB_DETAILS';
export const DOWNLOAD_LAB_DETAILS_SUCCESS = 'TEST_RESULTS.DOWNLOAD_LAB_DETAILS_SUCCESS';
export const DOWNLOAD_LAB_DETAILS_FAIL = 'TEST_RESULTS.DOWNLOAD_LAB_DETAILS_FAIL';

export const downloadLabDetails = (testResult: TestResult | TestResultFocused) => async (
  dispatch: Dispatch,
  getState: () => RootState
) => {
  const state = getState();
  const eventId = () => {
    if (testResult.category === 'Lab' && testResult.source === 'MILLENNIUM') {
      const pipedIds = testResult.componentEventIds
        .map(componentEventId => componentEventId)
        .join('|');

      return pipedIds;
    }

    return testResult.eventId;
  };

  // Check for in flight downloads if there are any then return
  if (labDetailsDownloadDataSelector(state)[eventId()] === 'pending') {
    return;
  }

  dispatch({ type: DOWNLOAD_LAB_DETAILS, payload: { id: testResult?.eventId } });

  const auditData = base64.encode(
    JSON.stringify({
      date: testResult.testDate,
      message: testResult.name
    })
  );
  const url = getDispatcherUrl(
    DispatcherCategoryService.HEALTH_RECORDS,
    DispatcherAuthPrefix.AUTHENTICATED,
    DispatcherResource.LABS,
    DispatcherEndpoint.ID_DOWNLOAD,
    true
  );
  const headers = {
    ...getDownloadHeadersWithCurrentAccountConsumerId(state, url),
    ...testResult?.mhpDetails.headers,
    [DFDHeaders.mhpAccept]: 'application/pdf'
  };

  try {
    const response = await FileServices.download(
      `lab-details-${Date.now().toString()}.pdf`,
      url,
      headers
    );
    dispatch({
      type: DOWNLOAD_LAB_DETAILS_SUCCESS,
      payload: { id: testResult?.eventId, response }
    });
  } catch (err) {
    dispatch({
      type: DOWNLOAD_LAB_DETAILS_FAIL,
      payload: { id: testResult?.eventId },
      error: err
    });
  }
};

/**
 * Share Lab Results
 */
export const SHARE_LAB_RESULTS = 'TEST_RESULTS.SHARE_LAB_RESULTS';
export const SHARE_LAB_RESULTS_SUCCESS = 'TEST_RESULTS.SHARE_LAB_RESULTS_SUCCESS';
export const SHARE_LAB_RESULTS_FAIL = 'TEST_RESULTS.SHARE_LAB_RESULTS_FAIL';

export const shareLabResults = (
  testResult: TestResult,
  toAddress: string,
  message: string,
  useSecureEmail: boolean,
  contactPhoneNumber: string
) => {
  const { adapterSource = AdapterSource.UTAH, eventId, source, name, testDate } = testResult;
  const auditData = base64.encode(
    JSON.stringify({
      date: testDate,
      message: name
    })
  );
  const data: ShareLabResults = {
    toAddress,
    message,
    useSecureEmail,
    contactPhoneNumber
  };
  let client = 'testResults';
  let url = `/lab/detail/share?source=${source}&eventId=${eventId}`;
  let headers: DFDHeadersObject = {
    [DFDHeaders.mhpAccept]: 'application/pdf'
  };
  if (!!Config.TEST_RESULTS_EXPANSION_ENABLED) {
    client = 'dispatcher';
    url = getDispatcherUrl(
      DispatcherCategoryService.HEALTH_RECORDS,
      DispatcherAuthPrefix.AUTHENTICATED,
      DispatcherResource.LABS,
      DispatcherEndpoint.ID_TRANSMIT
    );
    headers = {
      ...headers,
      [DFDHeaders.eventIds]: eventId,
      [DFDHeaders.filterSource]: source,
      [DFDHeaders.mhpAdapter]: adapterSource,
      [DFDHeaders.mhpAuditData]: auditData
    };
    data.subject = '';
  } else {
    headers[DFDHeaders.auditData] = auditData;
  }
  const dataObj = {
    type: SHARE_LAB_RESULTS,
    payload: {
      client,
      request: {
        method: 'post',
        headers,
        url,
        data
      }
    }
  };

  return dataObj;
};

export const GET_DOCUMENT = 'PATIENT_LETTER.GET_DOCUMENT';
export const GET_DOCUMENT_SUCCESS = 'PATIENT_LETTER.GET_DOCUMENT_SUCCESS';
export const GET_DOCUMENT_FAIL = 'PATIENT_LETTER.GET_DOCUMENT_FAIL';
export const getPatientLetter = (acceptType: string, documentId: string) =>
  getDocumentDecorator(GET_DOCUMENT)(documentId, acceptType);

/**
 * Download Lab details Patient letter
 */
export const downloadPatientLetter = (data: string) => async () => {
  try {
    await FileServices.saveDataToFile(`patient-letter-${Date.now().toString()}.pdf`, data);
  } catch (err) {
    Alert.alert(
      'Could not download file',
      'An error occurred while downloading this file. Please try again.'
    );
  }
};

export const DOWNLOAD_PATIENT_LETTERS = 'TEST_RESULTS.DOWNLOAD_PATIENT_LETTERS';
export const DOWNLOAD_PATIENT_LETTERS_SUCCESS = 'TEST_RESULTS.DOWNLOAD_PATIENT_LETTERS_SUCCESS';
export const DOWNLOAD_PATIENT_LETTERS_FAIL = 'TEST_RESULTS.DOWNLOAD_PATIENT_LETTERS_FAIL';
